// Serializes the leader roster to CSV. Pure: the rows, the translated labels
// and the export date all come in as arguments, so the mapping is testable
// without a DOM or a clock.
//
// The columns mirror the table, deliberately: a leader who filters the table
// and hits Export expects the file to say what the screen said. `joinedAt` is
// emitted as an ISO calendar date (UTC) rather than the display format — a
// spreadsheet sorts "2026-03-02" correctly and "Mar 2, 2026" not at all.

import { buildCsv } from '../../../lib/csv';
import type { GroupRosterMember } from '../../../lib/views/groups';

export interface RosterCsvLabels {
  readonly headers: {
    readonly firstName: string;
    readonly lastName: string;
    readonly email: string;
    readonly roleInGroup: string;
    readonly circles: string;
    readonly otherNetworks: string;
    readonly joined: string;
  };
  readonly roleLeader: string;
  readonly roleMember: string;
}

function isoDate(value: string): string {
  return value.slice(0, 10);
}

// Names only, `; `-joined — no commas, so the cell needs no quoting.
function names(items: ReadonlyArray<{ name: string }>): string {
  return items.map((i) => i.name).join('; ');
}

export function buildRosterCsv(
  rows: ReadonlyArray<GroupRosterMember>,
  labels: RosterCsvLabels,
): string {
  const h = labels.headers;
  return buildCsv(
    [
      h.firstName,
      h.lastName,
      h.email,
      h.roleInGroup,
      h.circles,
      h.otherNetworks,
      h.joined,
    ],
    rows.map((m) => [
      m.firstName,
      m.lastName,
      m.email,
      m.role === 'leader' ? labels.roleLeader : labels.roleMember,
      // Affiliations other than the group being exported, matching the table.
      names(m.circles.filter((c) => c.id !== m.groupId)),
      names(m.networks.filter((n) => !n.isThisGroup)),
      isoDate(m.joinedAt),
    ]),
  );
}

// "Boston Women in Tech" + 2026-08-11 → "boston-women-in-tech-members-2026-08-11.csv"
export function rosterCsvFilename(groupName: string, today: Date): string {
  const slug =
    groupName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'group';
  return `${slug}-members-${today.toISOString().slice(0, 10)}.csv`;
}
